import React from 'react';
import { ThermometerSun, Droplets, TestTube, Calendar } from 'lucide-react';

/**
 * Компонент для ввода симптомов овуляции
 */
const SymptomsSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  ovulationSymptoms 
}) => {
  return (
    <div className="mb-6 bg-rose-50 dark:bg-rose-900/20 p-4 rounded-lg border border-rose-200 dark:border-rose-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <ThermometerSun className="w-5 h-5 mr-2 text-rose-500" />
        Симптомы овуляции
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Базальная температура */}
        <div>
          <label className="inline-flex items-center mb-2">
            <input
              type="checkbox"
              name="includeBbt"
              checked={formData.includeBbt}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 font-medium">
              Учет базальной температуры
            </span>
          </label>
          {formData.includeBbt && (
            <div className="mt-2">
              <label htmlFor="bbtRise" className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                Повышение температуры (°C)
              </label>
              <div className="flex">
                <input
                  type="number"
                  id="bbtRise"
                  name="bbtRise"
                  value={formData.bbtRise}
                  onChange={handleInputChange}
                  min="0.2"
                  max="1"
                  step="0.1"
                  className={`form-input w-full ${errors.bbtRise ? 'input-error' : ''}`}
                  placeholder="Обычно 0.4°C"
                />
                <span className="inline-flex items-center px-3 text-gray-500 bg-gray-100 dark:bg-gray-600 dark:text-gray-300 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md">
                  °C
                </span>
              </div>
              {errors.bbtRise ? (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.bbtRise}
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Обычно повышение составляет 0.2-0.5°C после овуляции
                </p>
              )}
            </div>
          )}
        </div>

        {/* Цервикальная слизь */}
        <div>
          <label className="inline-flex items-center mb-2">
            <input
              type="checkbox"
              name="includeCervicalMucus"
              checked={formData.includeCervicalMucus}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 font-medium">
              Учет цервикальной слизи
            </span>
          </label>
          {formData.includeCervicalMucus && (
            <div className="mt-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-start">
                <Droplets className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0 mr-2" />
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    В период овуляции выделения становятся более прозрачными, скользкими и тянущимися, напоминают яичный белок.
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                    Этот симптом является одним из самых надежных природных индикаторов фертильности.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Дата положительного теста на овуляцию */}
        <div className="col-span-full">
          <div className="flex items-center mb-2">
            <TestTube className="w-5 h-5 text-rose-500 mr-2" />
            <label htmlFor="ovulationTestDate" className="text-sm text-gray-700 dark:text-gray-300 font-medium">
              Дата положительного теста на овуляцию (если проводился)
            </label>
          </div>
          <input
            type="date"
            id="ovulationTestDate"
            name="ovulationTestDate"
            value={formData.ovulationTestDate}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.ovulationTestDate ? 'input-error' : ''}`}
            max={new Date().toISOString().split('T')[0]} // Не позволяет выбрать будущую дату
          />
          {errors.ovulationTestDate ? (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.ovulationTestDate}
            </p>
          ) : (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Положительный тест на овуляцию означает, что овуляция, вероятно, произойдет в течение 24-48 часов
            </p>
          )}
        </div>

        {/* Другие симптомы */}
        <div className="col-span-full mt-2">
          <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-2 flex items-center">
            <Calendar className="w-4 h-4 mr-2 text-rose-500" />
            Другие симптомы, которые могут указывать на овуляцию:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {ovulationSymptoms.filter(symptom => 
              symptom.id !== 'cervicalMucus' && symptom.id !== 'bbtRise'
            ).map((symptom) => (
              <div 
                key={symptom.id}
                className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {symptom.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {symptom.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SymptomsSection;